import React from 'react';
import { Link } from 'react-router-dom';
import logo from '../../image/health-guide-logo.png';
import '../Page/LegalCard.css';

const CONTACT_EMAIL = 'healthguide@chantharzay.com';
const CONTACT_PHONE = '09-699101168';

const PrivacyPolicy = () => {
  return (
    <div className="hg-legal">
      <main className="hg-wrapper">
        <section className="hg-card">
          <header className="hg-header">
            <img src={logo} alt="Health Guide MM Logo" className="hg-logo" />
            <h1>Privacy Policy</h1>
            <div className="hg-updated">Last updated: October 29, 2026</div>
          </header>

          <p>
            Health Guide MM (ကျန်းမာလမ်းညွှန်) is operated by Chantharzay ("we", "our", or "us").
            This Privacy Policy explains what information the <strong>Health Guide MM</strong> mobile
            app for Android and iOS collects, how we use it, who we share it with, and how you can
            delete your account.
          </p>

          <h2>1. Information We Collect</h2>

          <h3>Information you give us</h3>
          <ul>
            <li><strong>Account information</strong> – phone number, username, password (stored only in hashed form), city and region.</li>
            <li><strong>Health information</strong> – your answers to symptom questions and the questions you ask the AI health guide.</li>
            <li><strong>Bookings</strong> – the clinic you choose and the patient name, age, city and region you enter.</li>
            <li><strong>Quiz and rewards</strong> – your quiz answers, points and redeemed rewards.</li>
          </ul>

          <h3>Information collected automatically</h3>
          <ul>
            <li><strong>Push notification token</strong> – so we can send you booking updates and health tips.</li>
            <li><strong>Advertising ID</strong> – the Google Advertising ID on Android, or the iOS advertising identifier if you allow tracking.</li>
            <li><strong>App events</strong> – actions such as installing or opening the app, completing registration, making a booking, starting an AI chat, completing a quiz and redeeming a reward.</li>
            <li><strong>Basic device information</strong> – device type, operating system and network status.</li>
          </ul>

          <h2>2. How We Use Your Information</h2>
          <ul>
            <li>To create and manage your account and let you log in.</li>
            <li>To give you AI-generated health guidance and suggest suitable specialists.</li>
            <li>To send your booking request to the clinic you choose.</li>
            <li>To run daily quizzes, points and rewards.</li>
            <li>To send notifications about your bookings, campaigns and health tips.</li>
            <li>To measure how well our advertising works and to improve the app.</li>
          </ul>

          <h2>3. Who We Share Information With</h2>
          <ul>
            <li>
              <strong>Clinics and hospitals</strong> – when you make a booking, the clinic receives the
              patient name, age, city and region you entered so they can respond to your request.
            </li>
            <li>
              <strong>Google Firebase</strong> – delivers push notifications. See{' '}
              <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">Firebase Privacy</a>.
            </li>
            <li>
              <strong>Meta (Facebook) App Events</strong> – we send app events (listed in section 1)
              together with your advertising ID, so we can measure our ads on Facebook and Instagram.
              See{' '}
              <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer">Meta Privacy Policy</a>.
            </li>
          </ul>

          <div className="hg-callout">
            <strong>We never share your health information for advertising.</strong> The events we
            send to Meta contain only the event name (for example "booking made"). They never include
            your symptoms, chat messages, patient name, age or the clinic you chose. We do not sell
            your personal information.
          </div>

          <h2>4. Advertising ID and Your Choices</h2>
          <ul>
            <li>
              <strong>Android:</strong> you can reset or delete your advertising ID in
              Settings → Google → Ads (or Settings → Privacy → Ads).
            </li>
            <li>
              <strong>iPhone:</strong> the app asks for your permission before tracking. If you choose
              "Ask App Not to Track", your advertising identifier is not shared. You can change this at
              any time in Settings → Privacy &amp; Security → Tracking.
            </li>
            <li>
              <strong>Notifications:</strong> you can turn them off at any time in your device settings.
            </li>
          </ul>

          <h2>5. Permissions Used</h2>
          <ul>
            <li><strong>Internet and network state</strong> – to connect to our servers and detect connectivity changes.</li>
            <li><strong>Notifications</strong> – to send booking updates and health tips.</li>
            <li><strong>Photo library (iOS)</strong> – only when you choose a picture yourself.</li>
            <li><strong>Advertising ID / App Tracking Transparency</strong> – for ad measurement, as described above.</li>
          </ul>

          <h2>6. Data Retention and Deletion</h2>
          <p>
            We keep your information while your account is active. You can ask us to delete your
            account and all related data at any time. See{' '}
            <Link to="/delete-account">Delete your Health Guide account</Link> for the steps.
          </p>

          <h2>7. Data Security</h2>
          <p>
            Data is sent between the app and our servers over encrypted connections (HTTPS), and
            passwords are stored only in hashed form. No digital system is completely secure, but we
            take reasonable technical measures to protect your information.
          </p>

          <h2>8. Medical Disclaimer</h2>
          <p>
            Health Guide MM gives general health information to help you decide where to seek care. It
            is <strong>not</strong> a diagnosis or a replacement for a doctor. In an emergency, contact
            emergency services or go to the nearest hospital.
          </p>

          <h2>9. Children's Privacy</h2>
          <p>
            Health Guide MM is not intended for children under 13 years of age, and we do not knowingly
            collect personal information from them. If we learn that we have, we will delete it promptly.
          </p>

          <h2>10. Policy Updates</h2>
          <p>
            We may update this Privacy Policy from time to time. Changes will be posted on this page
            with a new "Last updated" date.
          </p>

          <h2>11. Contact Us</h2>
          <p>For questions about this Privacy Policy or your data, please contact us:</p>
          <p>
            📧 <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            <br />
            📞 <a href={`tel:${CONTACT_PHONE.replace(/-/g, '')}`}>{CONTACT_PHONE}</a>
          </p>

          <footer className="hg-footer">© 2026 Health Guide MM by Chantharzay. All rights reserved.</footer>
        </section>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
